import React, {useEffect, useState} from "react";
import { toastr } from 'react-redux-toastr';
import apiClient from '../../gears/apiClient';
import { FormGroup } from 'reactstrap';
import PropTypes from 'prop-types'
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import ComponentLoader from '../ComponentLoader';
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../ui/select";

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
  action: Yup.string().required('Action is required'),
  active: Yup.boolean().required(),
  triggerUrl: Yup.array()
    .of(Yup.string().required('URL must be a string'))
    .min(1, 'At least one trigger URL is required')
    .required('Trigger URL is required'),
});

export function PopupCustomization({
  notification = null,
  setNotification,
  aiPrompt,
  setAiPrompt,
  onOpenChange,
  notificationDialogOpen,
  setNeedToUpdate
}) {
  const [configActions, setConfigActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAssistActions = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get('/assist-actions');
        setConfigActions(response.actions);
      } catch (e) {
        setConfigActions([]);
      }

      setIsLoading(false);
    }

    fetchAssistActions();
  }, []);

  const updateOrCreateConfig = async (data) => {
    setIsLoading(true);
    const request = notification
      ? apiClient.put(`/assist-config/${notification.id}`, data)
      : apiClient.post('/assist-config', data)

    try {
      await request;
      setNeedToUpdate(true);
      setNotification(null);
      notificationDialogOpen(false);
      toastr.success(`${notification ? 'Updated' : 'Created'} successfully`)
    } catch(e) {
      toastr.error(e?.error);
    }

    setIsLoading(false);
  }

  return (
    <div>
      {/*
        <h3 className="text-sm font-medium mb-3">
          AI Pop-up Customization
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Customize how the AI assistance pop-up appears to your users.
          The pop-up will display with your custom text and when users click
          the action button, they'll get AI help using your custom prompt.
        </p>
      */}

      <div className="grid gap-4">
        {isLoading && <ComponentLoader customClass="absolute bg-white/80 top-0 left-0 right-0 bottom-0 z-999 flex items-center justify-center" />}

        <Formik
          initialValues={{
            name: notification?.name || '',
            description: notification?.description || '',
            action: notification?.action || configActions[0],
            active: notification?.active ?? true,
            triggerUrl: notification?.triggerUrl || [''],
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => updateOrCreateConfig(values)}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, isValid, dirty, setFieldValue }) => (
            <Form id="assist-config-form" className="form">

              <FormGroup>
                <Label htmlFor="entry-name">Headline</Label>
                <Field
                  id="entry-name"
                  className="form-control mt-1"
                  type="text"
                  name="name"
                  placeholder="Need help with this page?"
                />
                {errors.name && touched.name &&
                  <div className="error text-xs mt-1">{errors.name}</div>
                }
              </FormGroup>

              <FormGroup>
                <Label htmlFor="entry-name">Subheadline</Label>
                <Field
                  id="entry-description"
                  className="form-control mt-1"
                  type="text"
                  name="description"
                  placeholder="Our AI assistant can help you navigate this page"
                />
                {errors.description && touched.description &&
                  <div className="error text-xs mt-1">
                    {errors.description}
                  </div>
                }
              </FormGroup>

              <FormGroup>
                <Label htmlFor="action">Action</Label>
                <Field
                  id="entry-action"
                  className="form-control mt-1"
                  as="select"
                  name="action"
                >
                  {configActions.map((action) => (
                    <option key={action} value={action}>
                      {action}
                    </option>
                  ))}
                </Field>
                {errors.action && touched.active &&
                  <div className="error text-xs mt-1">
                    {errors.action}
                  </div>
                }
              </FormGroup>

              {notification && (<FormGroup check className="mb-3">
                <Label check>
                  <Field type="checkbox" name="active" className="-mt-0 form-check-input" style={{ accentColor: 'black' }}/>
                  {' '}Activate Notification
                </Label>
                {errors.active && touched.active &&
                  <div className="error text-xs mt-1">
                    {errors.active}
                  </div>
                }
              </FormGroup>)}

              <FormGroup>
                <Label>Trigger URLs</Label>
                {values.triggerUrl.map((url, index) => (
                  <div key={index} className="d-flex align-items-center mb-1">
                    <Input
                      type="text"
                      name={`triggerUrl[${index}]`}
                      value={url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="form-control mt-1"
                    />
                    <Button
                      type="button"
                      className="btn btn-black ml-2"
                      onClick={() => {
                        const urls = [...values.triggerUrl];
                        urls.splice(index, 1);
                        setFieldValue('triggerUrl', urls);
                      }}
                      disabled={values.triggerUrl.length === 1}
                    >
                      &times;
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  className="btn btn-black mt-2"
                  onClick={() => setFieldValue('triggerUrl', [...values.triggerUrl, ''])}
                >
                  Add URL
                </Button>
                {errors.triggerUrl && touched.triggerUrl && typeof errors.triggerUrl === 'string' && (
                  <div className="error text-xs mt-1">
                    {errors.triggerUrl}
                  </div>
                )}
              </FormGroup>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2">
                <Button
                  className="text-black inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                  color="secondary"
                  type="button"
                  onClick={() => {
                    setNotification(null);
                    notificationDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || !dirty}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary-foreground h-10 px-4 py-2 bg-indigo-600 hover:bg-indigo-700"
                  color="primary"
                >
                  {notification ? 'Update' : 'Save'}
                </Button>

              </div>
            </Form>
          )}
        </Formik>

        {/*
          <div className="grid gap-2">
            <Label htmlFor="primaryButton">Primary Button Text</Label>
            <Input
              id="primaryButton"
              value={primaryButtonText}
              onChange={(e) => setPrimaryButtonText(e.target.value)}
              placeholder="Get AI Help"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="secondaryButton">Secondary Button Text</Label>
            <Input
              id="secondaryButton"
              value={secondaryButtonText}
              onChange={(e) => setSecondaryButtonText(e.target.value)}
              placeholder="No Thanks"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="aiPrompt">AI Prompt</Label>
            <Textarea
              id="aiPrompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="The user is viewing the page at {url}. Help them navigate and find what they need."
              className="min-h-[100px]"
            />
            <p className="text-xs text-gray-500">
              This is the prompt that will be sent to the AI when the user clicks the primary button.
              You can use {'{url}'} as a placeholder for the current page URL.
            </p>
          </div>

          <PopupPreview
            headline={headline}
            subheadline={subheadline}
            primaryButtonText={primaryButtonText}
            secondaryButtonText={secondaryButtonText}
          />
        */}

      </div>
    </div>
  );
}
